import { Fragment } from 'react';
import { Minifiable } from '@optimus-web/common';
import { OptimusTextKey } from '@optimus-web/localization';
import { Stack } from '../Stack';
import { Step, StepDescription } from './Step';
import { ProgressIndicatorContainer, StepCountElement, StepLineElement } from './style';

export interface ProgressIndicatorProps<S extends string = string> extends Minifiable {
    steps: StepDescription<S>[];
    activeStep?: S;
    onStepChange?: (value: S) => void;
    vertical?: boolean;
    isOpened?: boolean;
}

export const ProgressIndicator = <S extends string = string>({
    steps,
    activeStep,
    onStepChange,
    vertical,
    small,
    isOpened,
    ...other
}: ProgressIndicatorProps<S>) => {
    const activeItemIndex = steps.findIndex((item) => item.value === activeStep);
    const singleItem = steps.length === 1;

    return (
        <ProgressIndicatorContainer vertical={vertical} {...other}>
            {steps.map(({ value, completed, order, label, optional, editable, ariaLabel }, index) => {
                const isActive = activeStep === value;
                const isCompleted = completed || activeItemIndex > index;
                const isVerticalOpenActiveStep = vertical && isOpened && isActive;

                return (
                    <Fragment key={value}>
                        {index > 0 ? (
                            <StepLineElement aria-hidden primary={isActive || isCompleted} vertical={vertical} />
                        ) : null}

                        {isVerticalOpenActiveStep ? (
                            <Stack itemAlignment="center" distribution="space-between" spacing="400">
                                <Step
                                    index={order || index}
                                    value={value}
                                    onClick={onStepChange}
                                    completed={isCompleted}
                                    active={isActive}
                                    vertical={vertical}
                                    small={small}
                                    singleItem={singleItem}
                                    label={label}
                                    optional={optional}
                                    editable={editable}
                                    ariaLabel={ariaLabel}
                                />
                                <StepCountElement>
                                    {activeItemIndex + 1} {OptimusTextKey.Of.toLowerCase()} {steps.length}
                                </StepCountElement>
                            </Stack>
                        ) : (
                            <Step
                                index={order || index}
                                value={value}
                                onClick={onStepChange}
                                completed={isCompleted}
                                active={isActive}
                                vertical={vertical}
                                small={small}
                                singleItem={singleItem}
                                label={label}
                                optional={optional}
                                editable={editable}
                                ariaLabel={ariaLabel}
                            />
                        )}
                    </Fragment>
                );
            })}
        </ProgressIndicatorContainer>
    );
};
